import { HiX } from "react-icons/hi";

const MODES = [
  {
    label: "Context",
    color: "emerald",
    dotClass: "bg-emerald-500",
    badgeClass: "bg-emerald-50 text-emerald-700 border-emerald-200",
    headerClass: "text-emerald-700",
    description:
      "Any contextually appropriate answer is accepted. There is no fixed expected answer — the AI judges whether the student's response makes sense for the question.",
    note: "Leave the Answer field empty. The AI grades based on context alone.",
    examples: [
      {
        answer: "I want some chicken.",
        score: 95,
        reason: "Directly answers, full sentence, perfect grammar",
      },
      { answer: "I want chicken.", score: 82, reason: "Directly answers, nearly complete" },
      { answer: "Chicken.", score: 65, reason: "In context but single keyword only" },
      { answer: "I want to play football.", score: 20, reason: "Not related to the question" },
    ],
    question: "What do you want for lunch?",
  },
  {
    label: "Strict",
    color: "blue",
    dotClass: "bg-blue-500",
    badgeClass: "bg-blue-50 text-blue-700 border-blue-200",
    headerClass: "text-blue-700",
    description:
      "The student must use a specific keyword in their answer. They can form any sentence they like, but the keyword must be present. A grammatically perfect sentence with the wrong keyword still scores low.",
    note: 'Fill in the Answer field with just the keyword (e.g. "spaghetti"), not a full sentence.',
    examples: [
      { answer: "I want to eat spaghetti.", score: 100, reason: "Correct keyword + full sentence" },
      { answer: "Spaghetti.", score: 60, reason: "Correct keyword, no sentence structure" },
      {
        answer: "I want to eat hamburger.",
        score: 20,
        reason: "Wrong keyword, even though grammatically correct",
      },
      { answer: "Hamburger.", score: 10, reason: "Wrong keyword and no structure" },
    ],
    question: "What do you want to eat?",
    expectedAnswer: "spaghetti",
  },
  {
    label: "Grammar",
    color: "purple",
    dotClass: "bg-purple-500",
    badgeClass: "bg-purple-50 text-purple-700 border-purple-200",
    headerClass: "text-purple-700",
    description:
      "The student must use the same grammatical structure as the template. The content can vary, but the sentence pattern must match. Great for drilling specific grammar forms.",
    note: 'Fill in the Grammar Template field with the required sentence structure (e.g. "I am going to ...").',
    examples: [
      {
        answer: "I am going to school.",
        score: 100,
        reason: "Correct structure, complete sentence",
      },
      {
        answer: "I am going to eat lunch.",
        score: 95,
        reason: "Correct structure, complete sentence",
      },
      {
        answer: "I want to go to school.",
        score: 35,
        reason: "Wrong structure — uses 'I want to go' instead of 'I am going to'",
      },
      { answer: "Going to school.", score: 40, reason: "Partial structure, incomplete" },
    ],
    question: "Where are you going?",
    expectedAnswer: "I am going to ...",
  },
];

const SCORE_COLOR: Record<string, string> = {
  high: "text-green-600 bg-green-50 border-green-200",
  mid: "text-yellow-600 bg-yellow-50 border-yellow-200",
  low: "text-red-500 bg-red-50 border-red-200",
};

function scoreClass(score: number) {
  if (score >= 70) return SCORE_COLOR.high;
  if (score >= 40) return SCORE_COLOR.mid;
  return SCORE_COLOR.low;
}

export function GradingModeInfoModal({
  isOpen,
  onClose,
}: { isOpen: boolean; onClose: () => void }) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />

      <div
        className="relative bg-gradient-to-br from-white via-blue-50/30 to-purple-50/30 rounded-2xl shadow-2xl flex flex-col z-10 border border-white/50 overflow-hidden"
        style={{ width: 620, maxHeight: "85vh" }}
      >
        {/* Decorative orbs */}
        <div className="absolute -top-16 -right-16 w-36 h-36 bg-gradient-to-br from-blue-400/20 to-purple-400/20 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute -bottom-16 -left-16 w-36 h-36 bg-gradient-to-br from-purple-400/20 to-pink-400/20 rounded-full blur-3xl pointer-events-none" />

        {/* Header */}
        <div className="flex justify-between items-center px-6 py-4 border-b border-white/60 shrink-0 relative">
          <div>
            <h2 className="text-base font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              Grading Modes
            </h2>
            <p className="text-[11px] text-gray-500 mt-0.5">
              How each mode evaluates student answers
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center bg-white hover:bg-red-50 text-red-500 rounded-xl hover:shadow-md transition-all border border-gray-200"
          >
            <HiX />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 min-h-0 overflow-y-auto flex flex-col gap-4 p-6">
          {MODES.map((mode) => (
            <div
              key={mode.label}
              className="flex flex-col gap-3 p-4 rounded-2xl bg-white/70 border border-white/80 shadow-sm"
            >
              {/* Mode header */}
              <div className="flex items-center gap-2">
                <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${mode.dotClass}`} />
                <span className={`text-sm font-bold ${mode.headerClass}`}>{mode.label} Mode</span>
              </div>

              <p className="text-[11px] text-gray-600 leading-relaxed">{mode.description}</p>

              <div
                className={`text-[10px] font-medium px-3 py-1.5 rounded-lg border ${mode.badgeClass}`}
              >
                💡 {mode.note}
              </div>

              {/* Example */}
              <div className="flex flex-col gap-1.5">
                <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-[10px] text-gray-500">
                  <span>
                    <span className="font-semibold text-gray-700">Q:</span> {mode.question}
                  </span>
                  {"expectedAnswer" in mode && (
                    <span>
                      <span className="font-semibold text-gray-700">Template / Answer:</span>{" "}
                      {mode.expectedAnswer}
                    </span>
                  )}
                </div>

                <div className="flex flex-col gap-1 mt-0.5">
                  {mode.examples.map((ex) => (
                    <div key={ex.answer} className="flex items-start gap-2">
                      <span
                        className={`shrink-0 text-[10px] font-bold px-1.5 py-0.5 rounded border ${scoreClass(ex.score)}`}
                      >
                        {ex.score}
                      </span>
                      <div className="flex flex-col min-w-0">
                        <span className="text-[11px] font-medium text-gray-800">"{ex.answer}"</span>
                        <span className="text-[10px] text-gray-400">{ex.reason}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
